<template>
  <section class="personal-space-sidebar">
    <div
      v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_ACTIVITY)"
    >
      <p class="sidebar-title nds-text-p-small-bold">
        {{ $t('menu_group_my_activity') }}
      </p>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_SAVED_SEARCHES)"
        class="iconized"
        :to="{ path: '/personal-space/saved-searches'}"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-magnify</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_my_saved_searches') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_NOTIFICATIONS)"
        class="iconized"
        :to="{ path: '/personal-space/notifications'}"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-bell</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_notifications') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_REQUESTS)"
        class="iconized"
        :to="{ path: '/reports'}"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-binoculars</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_requests_and_inquiries') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_EXPORT) && can_export"
        class="iconized"
        to="/personal-space/exports"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-download</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_exports') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_CREDIT_CONSUMPTION)"
        class="iconized"
        to="/personal-space/credit-consumption"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-database</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_credit_consumption') }}</span>
      </router-link>
    </div>

    <div
      v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_PARAMETERS)"
      class="sidebar-title-block"
    >
      <p class="sidebar-title nds-text-p-small-bold">
        {{ $t('menu_group_my_account') }}
      </p>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_PROFILE_AND_AI)"
        class="iconized"
        to="/personal-space/profile"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-account</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ user_data?.can_see_ai ? $t('link_profile') : $t('link_profile_only') }}</span>
      </router-link>

      <router-link
        class="iconized"
        to="/personal-space/my-contract"
      >
        <span class="iconized-icon iconized-icon--width-15">
          <svg-element
            class="icon"
            icon="contract"
            size="12"
          />
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_my_contract') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_ACCOUNT_SEGMENTS)
          && $can('read', PERMISSIONS.BUSINESS_ALERTS)"
        class="iconized"
        to="/personal-space/company-segments"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-office-building-outline</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_company_segments') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_CONTACT_SEGMENTS)
          && $can('read', PERMISSIONS.BUSINESS_ALERTS)"
        class="iconized"
        to="/personal-space/contact-segments"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-card-account-details-outline</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_contact_segments') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_MY_ALERTS)"
        class="iconized"
        to="/personal-space/alerts"
      >
        <span class="iconized-icon">
          <v-icon size="small"> mdi-flash </v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_alerts') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_TEMPLATES)"
        class="iconized"
        :class="{'router-link-exact-active': is_templates_or_signature_route_active}"
        to="/personal-space/templates"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-file-document-outline</v-icon>
        </span>

        <span
          v-if="is_license_smart_campaign
            || is_license_smart_campaign_v2
            || is_license_sales_and_marketing
            || is_license_sales_and_marketing_v2"
          class="nds-text-p-medium sidebar-text"
        >
          {{ $t('link_templates_and_signatures') }}
        </span>
        <span
          v-else
          class="nds-text-p-medium sidebar-text"
        >
          {{ $t('link_templates_and_signature') }}
        </span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_TEMPLATES) && !is_license_sender && !is_license_sender_v2"
        class="iconized"
        to="/personal-space/bat"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-at</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_test_emails') }}</span>
      </router-link>
    </div>

    <div
      v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_MY_ACCOUNT)"
      class="sidebar-title-block"
    >
      <p class="sidebar-title nds-text-p-small-bold">
        {{ $t('menu_group_parameters') }}
      </p>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_EMAIL_CONNECTION)"
        class="iconized"
        :class="{'router-link-exact-active': is_connections_route_active}"
        to="/personal-space/connections"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-cog</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_external_connection') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_NOTIFICATIONS_SETTINGS)"
        class="iconized"
        to="/personal-space/notifications/settings"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-bell</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_notifications_settings') }}</span>
      </router-link>

      <router-link
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_CHANGE_PASSWORD)"
        class="iconized"
        to="/personal-space/change-password"
      >
        <span class="iconized-icon">
          <v-icon size="small">mdi-lock</v-icon>
        </span>
        <span class="nds-text-p-medium sidebar-text">{{ $t('link_password') }}</span>
      </router-link>

      <div class="sidebar-title-block">
        <a
          class="iconized"
          @click="goLogout()"
        >
          <span class="iconized-icon">
            <v-icon size="small">mdi-power</v-icon>
          </span>
          <span class="nds-text-p-medium sidebar-text">{{ $t('link_logout') }}</span>
        </a>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { PERMISSIONS, LICENSE_CONSTANTS } from '@/constants';

import configuration from '@/configurations';
import SvgElement from '@/components/elements/SvgElement.vue';

export default {
  name: 'PersonalSpaceSidebarElement',
  components: {
    SvgElement,
  },
  data: () => ({
    configuration,
    PERMISSIONS,
  }),
  computed: {
    ...mapGetters({
      user_data: 'user/userData',
    }),
    is_connections_route_active() {
      return ['EmailConnectionPage', 'LinkedinConnectionPage'].includes(this.$route.name);
    },
    is_templates_or_signature_route_active() {
      return this.$route.path.startsWith('/personal-space/templates')
        || this.$route.path.startsWith('/personal-space/signatures')
        || this.$route.path.startsWith('/personal-space/signatures-v2')
        || this.$route.path.startsWith('/personal-space/other-signatures');
    },
    can_export() {
      return this.user_data.can_export || this.user_data.can_export_full;
    },
    is_license_sales_and_marketing() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SALES_AND_MARKETING.ID;
    },
    is_license_smart_campaign() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.MARKETING.ID;
    },
    is_license_smart_campaign_v2() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SMART_CAMPAIGN_V2.ID;
    },
    is_license_sales_and_marketing_v2() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SALES_AND_MARKETING_V2.ID;
    },
    is_license_sender() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SENDER.ID;
    },
    is_license_sender_v2() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SENDER_V2.ID;
    },
  },
  methods: {
    ...mapActions({
      logout: 'user/logout',
    }),
    goLogout() {
      this.logout();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

.email-not-connected {
  margin-left: 70px;
}

.sidebar-title {
  color: $amethyst-smoke;
  margin: 0px 0 16px 0;
  text-transform: uppercase;
}

.sidebar-title-block {
  margin-top: 30px;
}

.iconized-icon {
  margin-right: 12px;
  text-align: center;

  &--width-15 {
    width: 15px;
  }
}

.iconized-lock-icon {
  margin-left: 4px;
  margin-right: 15px;
}

.iconized-chevron {
  margin-left: 5px;
}

.router-link-exact-active {
  border-right: 3px solid $cornflower-blue !important;

  .sidebar-text {
    color: $cornflower-blue !important;
  }

  &:deep(i) {
    color: $cornflower-blue !important;
  }

  &:deep(.icon) {
    --color: #{$cornflower-blue} !important;
  }
}

.iconized {
  margin-bottom: 16px;
  height: 16px;
  align-items: center;
  display: flex;
  outline: none;
  text-decoration: none;
  border: none;

  &:deep(i) {
    color: $amethyst-smoke;
  }

  &:deep(.icon) {
    --color: #{$amethyst-smoke};
  }

  &:deep(.email-not-connected) {
    color: $ripe-pumpkin !important;
  }

  &:hover {
    border-right: 3px solid $medium-slate-blue !important;

    .sidebar-text {
      color: $medium-slate-blue !important;
    }

    &:deep(i) {
      color: $medium-slate-blue !important;
    }

    &:deep(.icon) {
      --color: #{$medium-slate-blue} !important;
    }

    &:deep(.email-not-connected) {
      color: $ripe-pumpkin !important;
    }
  }
}
</style>

<i18n src="@/locales/common.json"></i18n>

<i18n src="@/locales/components/elements/personal-space-sidebar-element.json"></i18n>
